/**
 * Pure helpers for the catalog command palette: use-case vs keyword mode
 * detection, the instant local keyword filter, and relevance formatting. No
 * React, no I/O - unit-tested in `__tests__/catalogSearch.test.ts`.
 */

import type { CatalogSearchResult } from '../data/catalogSearch';
import { platformIdOf } from './catalogPlatform';
import { decimals } from './format';

/** The palette's discrete phases, each with its own body in the mockup. */
export type CatalogPhase = 'empty' | 'keyword' | 'searching' | 'results' | 'error';

/**
 * Derives the palette phase from the raw input signals. A submitted query that
 * matches the current trimmed text is "on submitted" (a ranked search is in
 * flight, failed, or done); otherwise a non-empty query is keyword mode and an
 * empty one is the collapsed empty state. Extracted from useCatalogSearch so the
 * phase machine is unit-testable.
 */
export const catalogPhase = ({
  trimmed,
  submitted,
  searching,
  hasSearchError,
}: {
  trimmed: string;
  submitted: string | null;
  searching: boolean;
  hasSearchError: boolean;
}): CatalogPhase => {
  const onSubmitted = submitted !== null && submitted === trimmed;
  if (onSubmitted) return searching ? 'searching' : hasSearchError ? 'error' : 'results';
  return trimmed ? 'keyword' : 'empty';
};

/** The listbox element id, wiring the combobox input to its option list (ARIA). */
export const LISTBOX_ID = 'catalog-search-listbox';

/** A stable, unique option id for a result row (ARIA active-descendant target). */
export const optionId = (slug: string): string => `catalog-opt-${slug}`;

/** The catalog detail page a result row navigates to, honoring the platform-id
 *  alias map (e.g. `twitter.*` -> `/api/x-twitter`). */
export const catalogResultHref = (slug: string): string => `/api/${platformIdOf(slug)}`;

/** The interruptible open/close height animation for the palette body: only
 *  grid-template-rows transitions, and it is disabled under reduced motion. */
export const DISCLOSURE_ANIM_CLASS =
  'grid transition-[grid-template-rows] duration-150 ease-out motion-reduce:transition-none';

/**
 * Instant substring filter over already-loaded catalog rows: matches the query
 * against each row's name, slug, description, and category. Empty query returns
 * the rows unchanged. Pure and synchronous - this is the cheap keyword path.
 */
export const keywordFilter = (
  rows: CatalogSearchResult[],
  query: string,
): CatalogSearchResult[] => {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) =>
    `${r.name} ${r.slug} ${r.description} ${r.category}`.toLowerCase().includes(q),
  );
};

/** A relevance score (0..1) as a 0..100 bar width, clamped. */
export const relevancePct = (score: number): number =>
  Math.max(0, Math.min(100, Math.round(score * 100)));

/** A relevance score (0..1) as a two-decimal label, e.g. `0.82`. */
export const relevanceLabel = (score: number): string => decimals(score, 2);
